// htop-style memory readout.
//
// Top bar: stacked used / buffers / cached / shmem segments with the
// label `<used> / <total> GiB`. Bottom bar: thinner Swp readout (only
// when the worker has /proc/meminfo data — macOS dev environments don't).
//
// Falls back gracefully:
//   - `mem.source === "proc"` → full breakdown.
//   - `mem.source === "os"` → 2-segment used/free with a small note.
//   - No `mem` data at all → a compact "no data" row.

import { cn } from "@veil/ui/lib/utils";

export interface MemMetrics {
	totalGiB: number;
	usedGiB: number;
	buffersGiB?: number;
	cachedGiB?: number;
	shmemGiB?: number;
	swapTotalGiB?: number;
	swapUsedGiB?: number;
	source: "proc" | "os";
}

interface Props {
	mem?: MemMetrics | null;
	className?: string;
}

interface SegmentDef {
	value: number;
	className: string;
	label: string;
}

function StackedBar({
	segments,
	total,
	height = "h-3",
}: {
	segments: SegmentDef[];
	total: number;
	height?: string;
}) {
	let offset = 0;
	const safeTotal = total > 0 ? total : 1;
	return (
		<div
			className={cn(
				"relative w-full overflow-hidden rounded-sm bg-muted/40",
				height,
			)}
		>
			{segments.map((seg) => {
				const pct = Math.max(0, Math.min(100, (seg.value / safeTotal) * 100));
				if (pct <= 0) return null;
				const left = offset;
				offset += pct;
				return (
					<div
						key={seg.label}
						className={cn("absolute inset-y-0", seg.className)}
						style={{ left: `${left}%`, width: `${pct}%` }}
						title={`${seg.label} ${seg.value.toFixed(2)} GiB`}
					/>
				);
			})}
		</div>
	);
}

export function MemoryBar({ mem, className }: Props) {
	const effective = mem ?? null;

	// No /proc/meminfo payload available for this snapshot.
	if (!effective) {
		return (
			<div className={cn("flex flex-col gap-1", className)}>
				<div className="flex items-baseline justify-between gap-2">
					<span className="text-[10px] uppercase text-muted-foreground">
						Memory
					</span>
					<span className="font-mono text-xs tabular-nums text-muted-foreground">
						No data
					</span>
				</div>
			</div>
		);
	}

	const isProc = effective.source === "proc";
	const memSegments: SegmentDef[] = isProc
		? [
				{
					value: effective.usedGiB,
					className: "bg-emerald-500",
					label: "used",
				},
				{
					value: effective.buffersGiB ?? 0,
					className: "bg-sky-500",
					label: "buffers",
				},
				{
					value: effective.cachedGiB ?? 0,
					className: "bg-amber-400",
					label: "cached",
				},
				{
					value: effective.shmemGiB ?? 0,
					className: "bg-violet-500",
					label: "shmem",
				},
			]
		: [
				{
					value: effective.usedGiB,
					className: "bg-emerald-500",
					label: "used",
				},
			];

	const showSwap =
		isProc &&
		typeof effective.swapTotalGiB === "number" &&
		effective.swapTotalGiB > 0;

	return (
		<div className={cn("flex flex-col gap-2", className)}>
			<div>
				<div className="flex items-baseline justify-between gap-2">
					<span className="text-[10px] uppercase text-muted-foreground">
						Mem
					</span>
					<span className="font-mono text-xs tabular-nums">
						{effective.usedGiB.toFixed(1)} / {effective.totalGiB.toFixed(1)} GiB
					</span>
				</div>
				<StackedBar total={effective.totalGiB} segments={memSegments} />
			</div>
			{showSwap && (
				<div>
					<div className="flex items-baseline justify-between gap-2">
						<span className="text-[10px] uppercase text-muted-foreground">
							Swp
						</span>
						<span className="font-mono text-[10px] tabular-nums">
							{(effective.swapUsedGiB ?? 0).toFixed(1)} /{" "}
							{(effective.swapTotalGiB ?? 0).toFixed(1)} GiB
						</span>
					</div>
					<StackedBar
						total={effective.swapTotalGiB ?? 0}
						segments={[
							{
								value: effective.swapUsedGiB ?? 0,
								className: "bg-rose-500",
								label: "swap",
							},
						]}
						height="h-1.5"
					/>
				</div>
			)}
			{!isProc && (
				<p className="text-[10px] text-muted-foreground">
					Limited mem stats — worker is not on Linux/proc.
				</p>
			)}
			{isProc && (
				<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] uppercase text-muted-foreground">
					<LegendSwatch className="bg-emerald-500" label="used" />
					<LegendSwatch className="bg-sky-500" label="buffers" />
					<LegendSwatch className="bg-amber-400" label="cached" />
					<LegendSwatch className="bg-violet-500" label="shmem" />
				</div>
			)}
		</div>
	);
}

function LegendSwatch({
	className,
	label,
}: {
	className: string;
	label: string;
}) {
	return (
		<span className="inline-flex items-center gap-1.5">
			<span className={cn("inline-block size-2 rounded-sm", className)} />
			{label}
		</span>
	);
}
